import { useEffect, useRef, useState } from "react";

// Address input + autocomplete dropdown for the AVM island — the search-phase
// content of the Home Value capture card (SSD-16 / SUR-2302). Presentational:
// owns only the transient keyboard-highlight (activeIndex); emits onChange /
// onSelect up to useAvmFlow. The card FRAME (dark translucent surface) is the
// `.ssc-hv-card` wrapper in the Liquid template; this renders the card's inner
// content: header, address field, CTA, and the trust badges.
//
// UX (SUR-2152): the dropdown opens the moment the visitor types — showing
// "Searching…" while ez is queried — and the first result is highlighted so
// Enter selects it immediately. Arrow keys move the highlight; Enter selects.
const ITEM_ID_PREFIX = "avm-suggestion-";
const DEFAULT_TRUST = ["No obligation", "100% free", "60 seconds or less"];

export default function AddressSearch({
  query,
  suggestions,
  searching,
  lookupError,
  ctaText,
  cardTitle,
  cardSubtitle,
  trustItems,
  consentText,
  onChange,
  onSelect,
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef(null);

  // Reset the highlight to the first row whenever the result set changes, so a
  // fresh search always lands on a selectable top row.
  useEffect(() => {
    setActiveIndex(0);
  }, [suggestions]);

  // Keep the highlighted row scrolled into view during keyboard navigation.
  useEffect(() => {
    const el = listRef.current?.children?.[activeIndex];
    if (el?.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const trimmed = query.trim();
  const hasResults = suggestions.length > 0;
  // Open as soon as there's input: show results, the "Searching…" state, a
  // short "keep typing" hint, or an empty result message.
  const showDropdown = trimmed.length > 0;
  const title = cardTitle || "Find your home's market value";
  const subtitle =
    cardSubtitle ||
    "Enter your address for a free, no-obligation estimate of your home's current market value based on real local data.";
  const trust = Array.isArray(trustItems) && trustItems.length ? trustItems : DEFAULT_TRUST;

  const handleKeyDown = (e) => {
    if (!hasResults) {
      // Enter with no results yet (still searching / too short) does nothing.
      if (e.key === "Enter") e.preventDefault();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const choice = suggestions[activeIndex];
      if (choice) onSelect(choice);
    }
  };

  return (
    <div style={{ textAlign: "left", color: "var(--ss-connect-text)" }}>
      <div style={{ marginBottom: "1.25rem" }}>
        <h3
          style={{
            fontFamily: "var(--ss-connect-font-heading, inherit)",
            fontWeight: 400,
            fontSize: "1.5rem",
            margin: "0 0 0.5rem",
            color: "var(--ss-connect-text)",
          }}
        >
          {title}
        </h3>
        <p style={{ margin: 0, fontSize: "0.9rem", opacity: 0.82, lineHeight: 1.5 }}>{subtitle}</p>
      </div>

      <label
        htmlFor="avm-address"
        style={{
          display: "block",
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          fontSize: "0.7rem",
          fontWeight: 500,
          marginBottom: "0.4rem",
          color: "var(--ss-connect-text)",
        }}
      >
        Address
      </label>
      <div style={{ position: "relative" }}>
        <input
          id="avm-address"
          type="text"
          value={query}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter your home address"
          aria-label="Property address"
          autoComplete="off"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls="avm-suggestions"
          aria-activedescendant={hasResults ? `${ITEM_ID_PREFIX}${activeIndex}` : undefined}
          style={{
            width: "100%",
            padding: "0.75rem 0.9rem",
            borderRadius: 4,
            border: "1px solid var(--ss-connect-border)",
            background: "var(--ss-connect-surface-3)",
            color: "var(--ss-connect-text)",
            fontSize: "0.95rem",
          }}
        />

        {showDropdown && (
          <div
            style={{
              margin: "0.35rem 0 0",
              background: "var(--ss-connect-surface-3)",
              border: "1px solid var(--ss-connect-border)",
              borderRadius: 4,
              boxShadow: "0 8px 28px rgba(0,0,0,0.35)",
              overflow: "hidden",
              position: "absolute",
              left: 0,
              right: 0,
              zIndex: 20,
            }}
          >
            {hasResults ? (
              <ul
                id="avm-suggestions"
                ref={listRef}
                role="listbox"
                style={{ listStyle: "none", margin: 0, padding: 0, maxHeight: 280, overflowY: "auto" }}
              >
                {suggestions.map((s, i) => (
                  <li key={s.c_id}>
                    <button
                      type="button"
                      id={`${ITEM_ID_PREFIX}${i}`}
                      role="option"
                      aria-selected={i === activeIndex}
                      onClick={() => onSelect(s)}
                      onMouseEnter={() => setActiveIndex(i)}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        padding: "0.6rem 0.9rem",
                        border: "none",
                        background: i === activeIndex ? "rgba(255,250,250,0.08)" : "transparent",
                        cursor: "pointer",
                        fontSize: "0.92rem",
                        color: "var(--ss-connect-text)",
                      }}
                    >
                      {s.label}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div
                id="avm-suggestions"
                role="status"
                aria-live="polite"
                style={{ padding: "0.7rem 0.9rem", fontSize: "0.9rem", color: "var(--ss-connect-text-muted)" }}
              >
                {searching
                  ? "Searching…"
                  : trimmed.length < 3
                    ? "Keep typing your address…"
                    : // An upstream failure must not read as "your address isn't
                      // in our data" — that dead-ends the visitor on a valid address.
                      lookupError || "No matching addresses found"}
              </div>
            )}
          </div>
        )}
      </div>

      <button
        type="button"
        className="ss-c-btn ss-c-btn--accent"
        disabled={!hasResults}
        onClick={() => suggestions[activeIndex] && onSelect(suggestions[activeIndex])}
        style={{
          width: "100%",
          marginTop: "0.9rem",
          opacity: hasResults ? 1 : 0.55,
          cursor: hasResults ? "pointer" : "default",
        }}
      >
        {ctaText || "Find My Home Value"}
      </button>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "0.5rem",
          marginTop: "0.9rem",
          flexWrap: "wrap",
        }}
      >
        {trust.map((label) => (
          <span
            key={label}
            style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontSize: "0.72rem", opacity: 0.85 }}
          >
            <span aria-hidden="true" style={{ color: "var(--ss-connect-primary)" }}>
              ✓
            </span>
            {label}
          </span>
        ))}
      </div>

      {consentText && (
        <p style={{ margin: "0.8rem 0 0", fontSize: "0.7rem", lineHeight: 1.45, opacity: 0.7 }}>{consentText}</p>
      )}
    </div>
  );
}
